import { toastr } from "react-redux-toastr";
import AuthService from "Lib/API/Auth/index";
import { useHistory } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import React, { useState, useEffect } from "react";
import { Popover, MenuItem } from "@material-ui/core";
import { ListItemIcon, ListItemText } from "@material-ui/core";
import { Drawer, Typography, Box, IconButton } from "@material-ui/core";
// component
import AlertDialog from "components/Dialog/index";
import LazyAvatar from "components/LazyAvatar/index";
import PastoralService from "Lib/API/PastoralApi/index";
import { ReactComponent as Delete } from "assets/icons/Delete.svg";
import { ReactComponent as Archive } from "assets/icons/archive.svg";
import { ReactComponent as ThreeDots } from "assets/icons/GrayThreeDots.svg";
import MemberEventDrawerContent from "pages/EasyCare/MemberEventDrawer/MemberEventDrawerContent";
// direct import
import Loader from "./Loader";
import Profile from "../profile/Profile";
import { useStyles } from "../profile/style";
import ContactActions from "../profile/ContactActions";
import { member_type_colors } from "../profile/helper";

export default function EventDrawer({
  open,
  onClose,
  account,
  selectedData,
  openNewTaskDrawer,
  eventOpenData,
  refetchCallback,
}) {
  const classes = useStyles();
  const history = useHistory();
  const colors = ["#4CAF50", "#2A99FA"];
  const { getAccessTokenSilently } = useAuth0();

  const [anchorEl, setAnchorEl] = useState(null);
  const [eventData, setEventData] = useState({});
  const [deleteEvent, setDelete] = useState(false);
  const [archiveEvent, setArchive] = useState(false);
  const [eventLoading, setLoading] = useState(false);
  const [contactData, setContactData] = useState(null);
  const [loading, setGetContactLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [archiveLoading, setArchiveLoading] = useState(false);

  const popupOpen = Boolean(anchorEl);
  const id = popupOpen ? "custom-popover" : undefined;

  const calculateProfileCompletion = (user) => {
    const {
      account,
      createdAt,
      id,
      member_id,
      profile_img,
      relationship,
      _id,
      backGround,
      account_id,
      organization,
      updatedAt,
      __v,
      ...data
    } = { ...(user ? user : {}) };
    const insertedValues = Object.values(data);
    const insertedValuesLng = insertedValues.filter((it) => {
      if (it !== "") {
        if (typeof it === "object") {
          if (it?.formattedAddress !== undefined) {
            return true;
          } else return false;
        }
        return true;
      } else return false;
    })?.length;
    return Math.round((insertedValuesLng / 23) * 100) > 100
      ? 100
      : Math.round((insertedValuesLng / 23) * 100);
  };

  // get profile data
  const getContactHandler = async () => {
    setGetContactLoading(true);
    const token = await getAccessTokenSilently();
    AuthService.getContactById(
      selectedData?.easy_contact_id ?? eventOpenData?.easy_contact_id,
      token
    )
      .then((res) => {
        if (!res?.length) return;
        const result = { ...res?.[0] };
        result.id = result._id;
        const random = Math.floor(Math.random() * 2);
        if (!result.profile_img) {
          result.backGround = colors[random];
        }
        result.profile_completion = calculateProfileCompletion(result);
        setContactData(result);
      })
      .catch((err) => {
        // Handle error
      })
      .finally(() => {
        setGetContactLoading(false);
      });
  };

  useEffect(() => {
    if (!account?.main_contact?.account_id) return;
    getContactHandler();
  }, [account?.main_contact?.account_id]);

  // get event data
  const getEventHandler = async () => {
    const token = await getAccessTokenSilently();
    setLoading(true);
    PastoralService.getEventById({ token, eventId: selectedData?.eventId })
      .then((res) => {
        if (res) {
          setEventData(res?.data);
        }
      })
      .catch((err) => {
        toastr.error("Error", err?.data?.message || "Something went wrong");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    selectedData?.eventId && getEventHandler();
  }, [selectedData?.eventId]);

  const handlePopoverClose = () => setAnchorEl(null);
  const handleClick = (event) => setAnchorEl(event.currentTarget);

  const actions = [
    {
      label: eventData?.archived === 1 ? "Move to Active" : "Archive",
      icon: <Archive className="svg-size" />,
      onClick: () => {
        setArchive(true);
        handlePopoverClose();
      },
    },
    {
      label: "Delete",
      icon: <Delete className="svg-size" />,
      onClick: () => {
        setDelete(true);
        handlePopoverClose();
      },
    },
  ];

  const deleteEventHandler = async () => {
    if (deleteLoading) return;
    setDeleteLoading(true);
    const token = await getAccessTokenSilently();
    PastoralService.deleteEvent({ token, eventId: selectedData?.eventId })
      .then((res) => {
        if (res) {
          toastr.success("Success", "Event Deleted Successfully!");
          refetchCallback();
          setDelete(false);
          onClose();
        }
      })
      .catch((err) => {
        toastr.error("Error", err?.data?.message || "Something went wrong");
      })
      .finally(() => {
        setDeleteLoading(false);
      });
  };

  const archiveEventById = async (eventData) => {
    const data = {
      archived: eventData?.archived === 1 ? false : true,
    };
    try {
      setArchiveLoading(true);
      const token = await getAccessTokenSilently();
      await PastoralService.updateEvent({
        data,
        token,
        eventId: eventData?.id,
      });
      toastr.success(
        "Success",
        `Event ${
          eventData?.archived === 1 ? "Move to Active" : "Archived"
        } Successfully!`
      );
    } catch (error) {
      toastr.error("Error", error?.data?.message || "Something went wrong!");
    } finally {
      setArchiveLoading(false);
      refetchCallback();
      onClose();
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      classes={{ paper: classes.drawerPaper }}
    >
      <Box display="flex" justifyContent="flex-end">
        <IconButton onClick={handleClick}>
          <ThreeDots />
        </IconButton>
      </Box>

      {loading ? (
        <Loader />
      ) : (
        <>
          <LazyAvatar
            src={contactData?.profile_img}
            alt={contactData?.full_name}
            fallback={contactData?.full_name?.charAt(0) ?? "?"}
            className={classes.avatar}
          />

          <Typography variant="h6" className={classes.userName}>
            {contactData?.full_name}
          </Typography>
          <Typography variant="body2" className={classes.userId}>
            Member {`#${contactData?.member_id}`}
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Box
              className="member-type"
              sx={{
                width: "auto",
                display: "flex",
                justifyContent: "center",
                paddingLeft: 20,
                paddingRight: 20,
              }}
              style={
                contactData?.account_permission === "admin"
                  ? member_type_colors[1]
                  : member_type_colors[0]
              }
            >
              {contactData?.account_permission === "admin"
                ? "Admin"
                : "Family member"}
            </Box>
          </Box>

          <ContactActions
            email={contactData?.email_address}
            phone={contactData?.mobile_number}
            content={contactData?.mobile_number}
          />

          {contactData?.profile_completion && (
            <Profile
              profile={contactData?.profile_completion || 0}
              handleClick={() => {
                history.push(
                  `/member-details/${contactData?.account_id}/family-member`,
                  contactData
                );
              }}
            />
          )}
          {!eventLoading && (
            <MemberEventDrawerContent
              contactId={eventData?.involvedContactId}
              eventId={selectedData?.eventId}
              eventType={eventData?.eventTypeName}
              eventDate={eventData?.date}
              status={eventData?.archived}
              description={eventData?.memo || ""}
              openNewTaskDrawer={openNewTaskDrawer}
              setCallFeedsAgain={refetchCallback}
              refetchEventCallback={() => getEventHandler()}
            />
          )}
          {popupOpen && (
            <Popover
              id={id}
              open={popupOpen}
              anchorEl={anchorEl}
              onClose={handlePopoverClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              PaperProps={{
                style: {
                  width: 200,
                  borderRadius: 8,
                  boxShadow:
                    "0px 4px 20px rgba(0, 0, 0, 0.05), 0px 2px 8px rgba(0, 0, 0, 0.1)",
                },
              }}
            >
              <Box p={1} display="flex" flexDirection="column">
                {actions?.map((action, idx) => (
                  <MenuItem
                    key={idx}
                    onClick={action.onClick}
                    style={{ borderRadius: 6, marginBottom: 5 }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <ListItemIcon style={{ minWidth: 26 }}>
                        {action?.icon}
                      </ListItemIcon>
                      <ListItemText primary={action?.label} />
                    </Box>
                  </MenuItem>
                ))}
              </Box>
            </Popover>
          )}
          {deleteEvent && (
            <AlertDialog
              open={deleteEvent}
              onDecline={() => setDelete(false)}
              onAccept={() => {
                deleteEventHandler();
              }}
              title="Are you sure?"
              description={"Do you want to delete this event?"}
              disabled={deleteLoading}
            />
          )}
          {archiveEvent && (
            <AlertDialog
              open={archiveEvent}
              onDecline={() => setArchive(false)}
              onAccept={() => {
                archiveEventById(eventData);
              }}
              title="Are you sure?"
              description={`Are you sure you want to ${
                eventData?.archived === 1 ? "reactivate" : "archive"
              }?`}
              disabled={archiveLoading}
            />
          )}
        </>
      )}
    </Drawer>
  );
}
